import React from 'react';
import classNames from 'classnames';
import PaginationOverflowItem from './PaginationOverflowItem';
import { PaginationItemsProps } from './PaginationItemsProps';
import range from './range';
import PaginationItem from './PaginationItem';

export default function PaginationItemsDefault({
  current,
  items,
  labels,
  itemLink: ItemLink,
  itemOnClick,
}: PaginationItemsProps): JSX.Element {
  // List all items if fewer than 7
  if (items <= 7) {
    return (
      <>
        {range(0, items).map((value) => {
          const displayCurrentIndex = value + 1;
          return (
            <PaginationItem key={`${value}-pagination-item`}>
              <ItemLink
                className={classNames('usa-pagination__button', { 'usa-current': value === current })}
                aria-label={`${labels.pageLabel} ${displayCurrentIndex}`}
                aria-current={value === current ? 'page' : undefined}
                page={value}
                onClick={() => itemOnClick(value)}
              >
                {displayCurrentIndex}
              </ItemLink>
            </PaginationItem>
          );
        })}
      </>
    );
  }

  // User is at the start of a long dataset
  // Example: 1, 2, 3, *4*, 5 … 8
  // Doesn't apply when user gets to 5 of 8
  if (current <= 3 && items >= 7) {
    return (
      <>
        {range(0, 5).map((value) => {
          const displayCurrentIndex = value + 1;
          return (
            <PaginationItem key={`${value}-pagination-item`}>
              <ItemLink
                className={classNames('usa-pagination__button', { 'usa-current': value === current })}
                aria-label={`${labels.pageLabel} ${displayCurrentIndex}`}
                aria-current={value === current ? 'page' : undefined}
                page={value}
                onClick={() => itemOnClick(value)}
              >
                {displayCurrentIndex}
              </ItemLink>
            </PaginationItem>
          );
        })}
        <PaginationOverflowItem />
        <PaginationItem>
          <ItemLink
            className="usa-pagination__button"
            aria-label={`${labels.lastItemLabel} ${labels.pageLabel} ${items}`}
            page={items - 1}
            onClick={() => itemOnClick(items - 1)}
          >
            {items}
          </ItemLink>
        </PaginationItem>
      </>
    );
  }
  // When user is close to the end of dataset
  // Example: 1 … 4, *5*, 6, 7, 8
  if (current >= items - 4) {
    return (
      <>
        <PaginationItem>
          <ItemLink
            className="usa-pagination__button"
            aria-label={`${labels.pageLabel} 1`}
            page={0}
            onClick={() => itemOnClick(0)}
          >
            1
          </ItemLink>
        </PaginationItem>
        <PaginationOverflowItem />
        {range(items - 5, items).map((value) => {
          const displayCurrentIndex = value + 1;
          return (
            <PaginationItem key={`${value}-pagination-item`}>
              <ItemLink
                className={classNames('usa-pagination__button', { 'usa-current': value === current })}
                aria-label={
                  displayCurrentIndex === items
                    ? `${labels.lastItemLabel} ${labels.pageLabel} ${displayCurrentIndex}`
                    : `${labels.pageLabel} ${displayCurrentIndex}`
                }
                aria-current={value === current ? 'page' : undefined}
                page={value}
                onClick={() => itemOnClick(value)}
              >
                {displayCurrentIndex}
              </ItemLink>
            </PaginationItem>
          );
        })}
      </>
    );
  }
  // Default case: Current - 1, Current, Current + 1
  // Example: 1 … 21, *22*, 23 … 50
  // Example: 1 … 4, *5*, 6 … 9
  return (
    <>
      <PaginationItem>
        <ItemLink
          className="usa-pagination__button"
          aria-label={`${labels.pageLabel} 1`}
          page={0}
          onClick={() => itemOnClick(0)}
        >
          1
        </ItemLink>
      </PaginationItem>
      <PaginationOverflowItem />
      {range(current - 1, current + 2).map((value) => {
        const displayCurrentIndex = value + 1;
        return (
          <PaginationItem key={`${value}-pagination-item`}>
            <ItemLink
              className={classNames('usa-pagination__button', { 'usa-current': value === current })}
              aria-label={`${labels.pageLabel} ${displayCurrentIndex}`}
              aria-current={value === current ? 'page' : undefined}
              page={value}
              onClick={() => itemOnClick(value)}
            >
              {displayCurrentIndex}
            </ItemLink>
          </PaginationItem>
        );
      })}
      <PaginationOverflowItem />
      <PaginationItem>
        <ItemLink
          className="usa-pagination__button"
          aria-label={`${labels.lastItemLabel} ${items}`}
          page={items - 1}
          onClick={() => itemOnClick(items - 1)}
        >
          {items}
        </ItemLink>
      </PaginationItem>
    </>
  );
}
